import ReferralInput from '@/components/_referrals/ReferralInput'
import { Button } from '@/components/_ui/Button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/_ui/NavigationMenu'
import { Skeleton } from '@/components/_ui/Skeleton'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import { cn } from '@/lib/utils'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'

import T from '@/components/Helpers/Translate'
import type { CosmicHeaderItemType } from '@/lib/cms/cosmic/types/CosmicHeaderType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { useNavigationDisableOverlappingEvents } from '@/lib/hooks/helpers/useNavigationDisableOverlappingEvents'
import { useRouter } from 'next/router'
import React, { useEffect, useCallback } from 'react'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { useFocusMode } from '@/lib/hooks/helpers/useFocusMode'
import { Combobox } from '@/components/_ui/Combobox'
import { isSupportedLanguage } from '@/lib/utils/localization/isSupportedLanguage'
import { useHandleChangeLocale } from '@/lib/hooks/helpers/useHandleChangeLocale'
import { getLanguagesByAlpha2CountryCode } from '@/lib/utils/localization/getLanguagesByAlpha2CountryCode'
import { languageTranslations } from '@/constants/pickerRegions/regions'
import { useDevice } from '@/lib/hooks/helpers/useDevice'
import AccountPopover from '../_shared/AccountDrawer'
import { CartBadge } from '../_shared/CartBadge'
import { HeaderSearchBar } from '../_shared/HeaderSearchBar'
import LoginPopover from '../_shared/LoginPopover'
import ReferredByPopover from '../_shared/ReferredByPopover'
import { DesktopCountrySelector } from './DesktopCountrySelector'
import DesktopMenuSubMenu from './DesktopMenuSubMenu'

const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'>
>(({ className, title, children, ...props }, ref) => (
  <li>
    <NavigationMenuLink asChild>
      <a
        ref={ref}
        className={cn(
          'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
          className,
        )}
        {...props}
      >
        <div className="text-sm font-medium leading-none">{title}</div>
        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
          {children}
        </p>
      </a>
    </NavigationMenuLink>
  </li>
))

ListItem.displayName = 'ListItem'

const DesktopHeader = () => {
  const {
    cms: { header },
    user: { isSignedIn, refName },
    app: { alpha2, language },
    setApp,
    user,
  } = useGlobalStore()
  const { gtm } = useGTM()
  const router = useRouter()

  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()
  const { asPath } = useRouter()
  const { disableEvent, targetRef } = useNavigationDisableOverlappingEvents()

  const { isFocusMode, isLoadingIsFocusMode } = useFocusMode()
  const { isMobile } = useDevice()
  const { handleChangeLocale } = useHandleChangeLocale()

  const languagesForCurrentCountry = getLanguagesByAlpha2CountryCode(
    alpha2,
  ).map((lang) => ({
    value: lang,
    label: languageTranslations[lang],
    displayLabel: lang.toUpperCase(),
  }))

  const handleNavClick = useCallback(() => {
    setApp({
      isSearchBarActive: false,
      searchBarValue: '',
    })
  }, [setApp])

  useEffect(() => {
    router.events.on('beforeHistoryChange', handleNavClick)
    router.events.on('hashChangeStart', handleNavClick)

    return () => {
      router.events.off('beforeHistoryChange', handleNavClick)
      router.events.off('hashChangeStart', handleNavClick)
    }
  }, [router, handleNavClick])

  const navigationItem = ({
    item,
    index,
  }: {
    item: CosmicHeaderItemType
    index: number
  }) => {
    const { href, title, content, withContent } = item.headerItem
    const isActiveItem = parseLinkUrlWithLocale({ url: href }) === asPath

    if (!withContent) {
      return (
        <a
          href={parseLinkUrlWithLocale({ url: href })}
          tabIndex={-1}
          onClick={handleNavClick}
        >
          <NavigationMenuItem
            className={`xl:ml-8 ${isActiveItem && 'rounded-full bg-[#CAE8E6]'}`}
            onClick={() => {
              gtm.navbarClicked({
                action: item.headerItem.title,
              })

              mixpanelService
                .trackEvent(MixpanelEvent.NAVIGATION_BAR_CLICKED, {
                  action: item.headerItem.title,
                })
                .catch(() => {})
            }}
          >
            <Button className="text-base font-medium" variant="ghost">
              {title}
            </Button>
          </NavigationMenuItem>
        </a>
      )
    }

    return (
      <NavigationMenuItem className="xl:ml-8">
        <NavigationMenuTrigger
          className={`rounded-full text-base font-medium ${
            isActiveItem && 'bg-[#CAE8E6]'
          }`}
          ref={(ref) => {
            targetRef.current[index + 1] = ref
          }}
          onClick={(event) => {
            if (disableEvent) {
              event.preventDefault()
            }
          }}
          onMouseEnter={() => {
            const leftSectionPromotion = content?.leftSection.promotion
            const rightSectionPromotion = content?.rightSection.promotion

            if (leftSectionPromotion) {
              gtm.navbarClicked({
                action: title,
                ads_title: leftSectionPromotion.title,
                ads_image_name: leftSectionPromotion.image,
                ads_url: leftSectionPromotion.button.href,
              })

              mixpanelService
                .trackEvent(MixpanelEvent.NAVIGATION_BAR_CLICKED, {
                  action: title,
                  ads_title: leftSectionPromotion.title,
                  ads_image_name: leftSectionPromotion.image,
                  ads_url: leftSectionPromotion.button.href,
                })
                .catch(() => {})
            }

            if (rightSectionPromotion) {
              gtm.navbarClicked({
                action: title,
                ads_title: rightSectionPromotion.title,
                ads_image_name: rightSectionPromotion.image,
                ads_url: rightSectionPromotion.button.href,
              })

              mixpanelService
                .trackEvent(MixpanelEvent.NAVIGATION_BAR_CLICKED, {
                  action: title,
                  ads_title: rightSectionPromotion.title,
                  ads_image_name: rightSectionPromotion.image,
                  ads_url: rightSectionPromotion.button.href,
                })
                .catch(() => {})
            }
          }}
        >
          <a
            className="no-underline"
            href={parseLinkUrlWithLocale({ url: href })}
            tabIndex={-1}
            onClick={handleNavClick}
          >
            {title}
          </a>
        </NavigationMenuTrigger>

        <NavigationMenuContent>
          <DesktopMenuSubMenu
            rightSection={content?.rightSection}
            leftSection={content?.leftSection}
          />
        </NavigationMenuContent>
      </NavigationMenuItem>
    )
  }

  const renderNavigationItems = () => {
    if (header === 'error') {
      return (
        <div className="flex flex-col items-center justify-center">
          <Image
            className="px-3"
            alt="Unicity"
            src="/unicity.svg"
            width={160}
            height={25}
          />
          <span className="text-sm text-gray-500">
            We encountered an error loading our menu, we are working to fix it
          </span>
        </div>
      )
    }

    if (header === 'loading') {
      const skeletonItem = () => (
        <Skeleton className="mx-2 h-[35px] w-[200px] rounded-sm" />
      )

      return (
        <div className="flex items-center justify-center">
          {skeletonItem()}
          {skeletonItem()}

          <Image
            className="px-3"
            alt="Unicity"
            src="/unicity.svg"
            width={160}
            height={25}
          />
          {skeletonItem()}
          {skeletonItem()}
        </div>
      )
    }

    return (
      <div className="col-span-5 flex items-center gap-4 xl:gap-10">
        <a
          href={!isFocusMode ? parseLinkUrlWithLocale({ url: '/' }) : undefined}
          className={cn(
            'outline-offset-4',
            isFocusMode && 'pointer-events-none cursor-default text-gray-400',
          )}
          onClick={handleNavClick}
        >
          <Image
            className="ml-12 px-3"
            alt="Unicity"
            src="/unicity.svg"
            width={160}
            height={25}
          />
        </a>
        {!isFocusMode && !isLoadingIsFocusMode && (
          <NavigationMenuList className="md:mr-4 ">
            {header.items &&
              header.items.map((item, index) => {
                const { title = 'Unicity', href = '' } = item.headerItem || {}

                return (
                  <div key={`${title}-${href}`}>
                    {navigationItem({ item, index })}
                  </div>
                )
              })}
          </NavigationMenuList>
        )}
      </div>
    )
  }

  const displayCorrectReferralCode = () => {
    if (isSignedIn) return <div className="col-span-2" />

    if (user.refID && user.refName) {
      return (
        <div className="col-span-2 mb-1 mr-4 flex flex-col items-end">
          <div>
            <span className="text-xs leading-6">
              <T>referred_by</T>
            </span>
            <div className="-mt-2 flex items-center gap-x-2">
              <span className="text-sm font-bold">{refName}</span>
              <ReferredByPopover />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="col-span-2 mx-4 flex">
        <ReferralInput />
      </div>
    )
  }

  if (isMobile) return null

  return (
    <NavigationMenu>
      <div
        style={{ zIndex: 1000 }}
        className="relative w-[100vw] overflow-hidden bg-white"
      >
        <div className="flex items-center justify-between p-5">
          {renderNavigationItems()}
          <div className="flex items-center">
            {displayCorrectReferralCode()}
            <div className="pointer-events-none flex items-center space-x-1">
              <NavigationMenuItem
                className={cn(
                  'pointer-events-auto list-none',
                  isFocusMode && 'pointer-events-none',
                )}
              >
                <NavigationMenuTrigger
                  hasChevronDownIcon={!isFocusMode && !isLoadingIsFocusMode}
                  className="flex justify-center rounded-full px-1"
                  ref={(ref) => {
                    targetRef.current[0] = ref
                  }}
                  onClick={(event) => {
                    if (disableEvent) {
                      event.preventDefault()
                    }
                  }}
                >
                  <Image
                    className="mx-2"
                    src={`/flags/${alpha2}.svg`}
                    alt="Country Selector"
                    height={25}
                    width={25}
                  />
                </NavigationMenuTrigger>

                <NavigationMenuContent>
                  <DesktopCountrySelector />
                </NavigationMenuContent>
              </NavigationMenuItem>

              {isLoadingIsFocusMode && (
                <Skeleton className="h-[36px] w-[60px] rounded-sm" />
              )}

              {isFocusMode && (
                <Combobox
                  items={languagesForCurrentCountry}
                  value={language}
                  // placeholder={translate('select_country')}
                  // emptyResultsMessage={translate('country_not_found')}
                  className="pointer-events-auto h-auto w-auto justify-start border-0 p-2"
                  onChange={async (currentValue) => {
                    const currentValueLowercased = currentValue.toLowerCase()

                    if (!isSupportedLanguage(currentValueLowercased)) return

                    await handleChangeLocale({
                      newAlpha2: alpha2,
                      newLanguage: currentValueLowercased,
                      isRedirectToHome: false,
                    })
                  }}
                />
              )}

              {!isFocusMode && !isLoadingIsFocusMode && (
                <Button
                  onClick={() => {
                    setApp({ isSearchBarActive: true })
                    gtm.navbarSearchClicked()

                    mixpanelService
                      .trackEvent(
                        MixpanelEvent.NAVIGATION_BAR_SEARCH_CLICKED,
                        {},
                      )
                      .catch(() => {})
                  }}
                  className="pointer-events-auto"
                  size="icon"
                  variant="icon"
                >
                  <Image
                    src="/icons/search.svg"
                    alt="Search"
                    height={25}
                    width={25}
                  />
                </Button>
              )}

              {isSignedIn && <AccountPopover size={30} />}
              {/* Don't show login popover if signed out, if in focus mode or if loading focus mode */}
              {!isSignedIn && !isFocusMode && !isLoadingIsFocusMode && (
                <LoginPopover />
              )}

              <Button
                className="pointer-events-auto"
                onClick={() => {
                  setApp({ isCartOpen: true })
                }}
                size="icon"
                variant="icon"
              >
                <Image
                  src="/icons/empty-cart.svg"
                  alt="Cart"
                  height={25}
                  width={25}
                />
                <CartBadge />
              </Button>
            </div>
          </div>
        </div>
        <HeaderSearchBar />
      </div>
    </NavigationMenu>
  )
}
export default DesktopHeader
